import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CanvasGraph } from '../types';

export interface UseCanvasSearchOptions {
  canvasGraph: CanvasGraph;
}

export function useCanvasSearch({ canvasGraph }: UseCanvasSearchOptions) {
  const [canvasSearchQuery, setCanvasSearchQuery] = useState('');
  const [canvasFilter, setCanvasFilter] = useState<
    'all' | 'unmapped' | 'low-confidence' | 'mapped'
  >('all');
  const [canvasSearchMatchIndex, setCanvasSearchMatchIndex] = useState(0);
  const [focusNodeId, setFocusNodeId] = useState<string | null>(null);

  // Debounce the internal search query to avoid re-computing results on every keystroke
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(canvasSearchQuery);
    }, 200);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [canvasSearchQuery]);

  const canvasSearchResults = useMemo(() => {
    const results: string[] = [];
    if (!canvasGraph.nodes.length) return results;

    const query = debouncedQuery.trim().toLowerCase();

    for (const node of canvasGraph.nodes) {
      const kind = node.data?.kind;
      if (kind !== 'sourceField' && kind !== 'targetField') continue;

      if (canvasFilter === 'unmapped' && !node.data?.isUnmapped) continue;
      if (canvasFilter === 'mapped' && node.data?.isUnmapped) continue;
      if (canvasFilter === 'low-confidence') {
        const conf = node.data?.confidence ?? 1;
        if (conf >= 0.6) continue;
      }

      if (query) {
        const fieldName = (node.data?.field || node.data?.label || '').toLowerCase();
        const tableName = (node.data?.table || '').toLowerCase();
        if (!fieldName.includes(query) && !tableName.includes(query)) continue;
      }

      results.push(node.id);
    }
    return results;
  }, [canvasGraph.nodes, debouncedQuery, canvasFilter]);

  const canvasHighlightNodeIds = useMemo(() => {
    if (!debouncedQuery.trim() && canvasFilter === 'all') return undefined;
    return new Set(canvasSearchResults);
  }, [canvasSearchResults, debouncedQuery, canvasFilter]);

  const handleSearchNavigate = useCallback(
    (direction: 'next' | 'prev') => {
      if (canvasSearchResults.length === 0) return;
      let idx = canvasSearchMatchIndex;
      if (direction === 'next') {
        idx = (idx + 1) % canvasSearchResults.length;
      } else {
        idx = (idx - 1 + canvasSearchResults.length) % canvasSearchResults.length;
      }
      setCanvasSearchMatchIndex(idx);
      setFocusNodeId(canvasSearchResults[idx]);
    },
    [canvasSearchResults, canvasSearchMatchIndex],
  );

  // Reset match index when results change — debounce focus to avoid rapid viewport animations
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setCanvasSearchMatchIndex(0);
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    if (
      canvasSearchResults.length > 0 &&
      (debouncedQuery.trim() || canvasFilter !== 'all')
    ) {
      focusTimerRef.current = setTimeout(() => {
        setFocusNodeId(canvasSearchResults[0]);
      }, 150);
    }
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    };
  }, [canvasSearchResults.length, debouncedQuery, canvasFilter]);

  return {
    canvasSearchQuery,
    setCanvasSearchQuery,
    canvasFilter,
    setCanvasFilter,
    canvasSearchMatchIndex,
    canvasSearchResults,
    canvasHighlightNodeIds,
    focusNodeId,
    setFocusNodeId,
    handleSearchNavigate,
  };
}
